/**
 * ATS Report V3 - Main Component
 *
 * Renders the ATS analysis report in one of three display modes:
 *
 * - **compact**: Onboarding view. ScoreCircle (md) + summary text +
 *   3 category bars + critical issues count.
 *
 * - **full**: ATS tab view. ScoreCircle (lg) + summary + category bars.
 *   For full-tier data: all sections, errors, improvements, skills analysis,
 *   bullet rewrites. For quick-tier: categories + critical issues +
 *   "Loading detailed analysis..." placeholder.
 *
 * - **card**: Web-app card. ScoreCircle (sm) + overall score label +
 *   category scores inline.
 */

import React from 'react';
import { Target, Trophy, Zap, AlertTriangle, Loader2 } from 'lucide-react';
import { cx, getScoreLabel, getScoreVariant } from './utils/score-helpers';
import { isFullReport } from '../../types/ats-report';
import type { ATSReportProps } from './types';
import type { ATSReportV3Full } from '../../types/ats-report';

import { ScoreCircle } from './components/ScoreCircle';
import { CategoryBar } from './components/CategoryBar';
import { SectionDetail } from './components/SectionDetail';
import { BulletFeedback } from './components/BulletFeedback';
import { ErrorsList } from './components/ErrorsList';
import { ImprovementsList } from './components/ImprovementsList';
import { SkillsAnalysis } from './components/SkillsAnalysis';
import { ScoreInfoBanner } from './components/ScoreInfoBanner';
import { KeywordDemandInsights } from './components/KeywordDemandInsights';

import './ats-report.css';

// ---------------------------------------------------------------------------
// Category icon mapping
// ---------------------------------------------------------------------------

const categoryIcons = {
  completeness: <Target className="ats-report-category-bar__lucide" />,
  roleAlignment: <Trophy className="ats-report-category-bar__lucide" />,
  impact: <Zap className="ats-report-category-bar__lucide" />,
};

// ---------------------------------------------------------------------------
// Compact mode
// ---------------------------------------------------------------------------

const CompactView: React.FC<ATSReportProps> = ({ data, className }) => {
  const { report } = data;
  const criticalCount = report.criticalIssues.length;

  return (
    <div className={cx('ats-report ats-report--compact', className)}>
      {/* Hero: score circle + summary */}
      <div className="ats-report__hero">
        <ScoreCircle
          score={report.overallScore}
          label={getScoreLabel(report.overallScore)}
          size="md"
        />
        <div className="ats-report__hero-body">
          <p className="ats-report__summary">{report.summary}</p>
          {criticalCount > 0 && (
            <div className="ats-report__critical-badge">
              <AlertTriangle className="ats-report__critical-icon" />
              <span>
                {criticalCount} critical {criticalCount === 1 ? 'issue' : 'issues'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Category bars */}
      <div className="ats-report__categories">
        <CategoryBar
          label={report.categories.completeness.label}
          score={report.categories.completeness.score}
          weight={report.categories.completeness.weight}
          icon={categoryIcons.completeness}
        />
        <CategoryBar
          label={report.categories.roleAlignment.label}
          score={report.categories.roleAlignment.score}
          weight={report.categories.roleAlignment.weight}
          icon={categoryIcons.roleAlignment}
        />
        <CategoryBar
          label={report.categories.impact.label}
          score={report.categories.impact.score}
          weight={report.categories.impact.weight}
          icon={categoryIcons.impact}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Full mode
// ---------------------------------------------------------------------------

const FullView: React.FC<ATSReportProps> = ({ data, className, backendUrl }) => {
  const { report, formatScore, formatIssues } = data;
  const isFull = isFullReport(report);
  const fullReport = isFull ? (report as ATSReportV3Full) : null;

  return (
    <div className={cx('ats-report ats-report--full', className)}>
      {/* Educational banner: ATS Score vs Match Score */}
      <ScoreInfoBanner />

      {/* Hero */}
      <div className="ats-report__hero ats-report__hero--full">
        <ScoreCircle
          score={report.overallScore}
          label={getScoreLabel(report.overallScore)}
          size="lg"
        />
        <div className="ats-report__hero-body">
          <p className="ats-report__summary">{report.summary}</p>
          {report.detectedRole && (
            <div className="ats-report__detected-role">
              Detected Role: <strong>{report.detectedRole}</strong>
            </div>
          )}
        </div>
      </div>

      {/* Format score */}
      {formatScore !== undefined && (
        <div className={cx(
          'ats-report__format-card',
          `ats-report__format-card--${getScoreVariant(formatScore)}`,
        )}>
          <div className="ats-report__format-header">
            <span className="ats-report__format-label">Format Score</span>
            <span className="ats-report__format-value">{formatScore}</span>
          </div>
          {formatIssues.length > 0 && (
            <ul className="ats-report__format-issues">
              {formatIssues.map((issue, i) => (
                <li key={i} className="ats-report__format-issue">{issue}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Category bars */}
      <div className="ats-report__categories">
        <CategoryBar
          label={report.categories.completeness.label}
          score={report.categories.completeness.score}
          weight={report.categories.completeness.weight}
          icon={categoryIcons.completeness}
        />
        <CategoryBar
          label={report.categories.roleAlignment.label}
          score={report.categories.roleAlignment.score}
          weight={report.categories.roleAlignment.weight}
          icon={categoryIcons.roleAlignment}
        />
        <CategoryBar
          label={report.categories.impact.label}
          score={report.categories.impact.score}
          weight={report.categories.impact.weight}
          icon={categoryIcons.impact}
        />
      </div>

      {/* Critical issues (always shown) */}
      {report.criticalIssues.length > 0 && (
        <div className="ats-report__critical-issues">
          <h4 className="ats-report__critical-issues-title">
            <AlertTriangle className="ats-report__critical-issues-icon" />
            Critical Issues ({report.criticalIssues.length})
          </h4>
          <ul className="ats-report__critical-issues-list">
            {report.criticalIssues.map((issue, i) => (
              <li key={i} className="ats-report__critical-issue-item">{issue}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Full-tier content */}
      {fullReport ? (
        <>
          {/* Improvements (actionable items — shown right after critical issues) */}
          <ImprovementsList improvements={fullReport.improvements} />

          {/* Section details */}
          <div className="ats-report__sections">
            <SectionDetail
              section={fullReport.sections.contact}
              defaultExpanded={fullReport.sections.contact.score < 6}
            />
            <SectionDetail
              section={fullReport.sections.summary}
              defaultExpanded={fullReport.sections.summary.score < 6}
            />
            <SectionDetail
              section={fullReport.sections.experience}
              defaultExpanded={fullReport.sections.experience.score < 6}
            />
            <SectionDetail
              section={fullReport.sections.skills}
              defaultExpanded={fullReport.sections.skills.score < 6}
            />
            <SectionDetail
              section={fullReport.sections.education}
              defaultExpanded={fullReport.sections.education.score < 6}
            />
          </div>

          {/* Skills analysis */}
          <SkillsAnalysis skills={fullReport.sections.skills} />

          {/* Keyword market demand insights */}
          {backendUrl && fullReport.sections.skills.missingSkills.length > 0 && (
            <KeywordDemandInsights
              missingSkills={fullReport.sections.skills.missingSkills}
              backendUrl={backendUrl}
            />
          )}

          {/* Bullet rewrites from experience roles */}
          {fullReport.sections.experience.roles.length > 0 && (
            <div className="ats-report__bullet-rewrites">
              <h4 className="ats-report__bullet-rewrites-title">Bullet Rewrites</h4>
              {fullReport.sections.experience.roles.map((role, ri) =>
                role.bulletFeedback?.map((bf, bi) => (
                  <BulletFeedback
                    key={`${ri}-${bi}`}
                    original={bf.original}
                    rewritten={bf.rewritten}
                    reason={bf.reason}
                  />
                )),
              )}
              {/* Summary rewrites */}
              {fullReport.sections.summary.rewrites?.map((bf, i) => (
                <BulletFeedback
                  key={`summary-${i}`}
                  original={bf.original}
                  rewritten={bf.rewritten}
                  reason={bf.reason}
                />
              ))}
            </div>
          )}

          {/* Errors */}
          <ErrorsList errors={fullReport.errors} />
        </>
      ) : (
        /* Quick-tier placeholder */
        <div className="ats-report__loading-detail">
          <Loader2 className="ats-report__loading-spinner" />
          <span className="ats-report__loading-text">
            Loading detailed analysis...
          </span>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Card mode
// ---------------------------------------------------------------------------

const CardView: React.FC<ATSReportProps> = ({ data, className }) => {
  const { report } = data;

  return (
    <div className={cx('ats-report ats-report--card', className)}>
      <div className="ats-report__card-layout">
        <ScoreCircle
          score={report.overallScore}
          size="sm"
        />
        <div className="ats-report__card-body">
          <span className={cx(
            'ats-report__card-label',
            `ats-report__card-label--${getScoreVariant(report.overallScore)}`,
          )}>
            {getScoreLabel(report.overallScore)}
          </span>
          <div className="ats-report__card-categories">
            <span className="ats-report__card-cat">
              {categoryIcons.completeness}
              {report.categories.completeness.score}/10
            </span>
            <span className="ats-report__card-cat">
              {categoryIcons.roleAlignment}
              {report.categories.roleAlignment.score}/10
            </span>
            <span className="ats-report__card-cat">
              {categoryIcons.impact}
              {report.categories.impact.score}/10
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main dispatcher
// ---------------------------------------------------------------------------

export const ATSReport: React.FC<ATSReportProps> = (props) => {
  const { mode = 'full' } = props;

  switch (mode) {
    case 'compact':
      return <CompactView {...props} />;
    case 'card':
      return <CardView {...props} />;
    case 'full':
    default:
      return <FullView {...props} />;
  }
};

ATSReport.displayName = 'ATSReport';

export default ATSReport;
